import { useState } from "react";
import * as yup from "yup";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import AxiosInstance from "@/utils/AxiosInstance";
import MyDialogContent from "@/components/MyDialogContent";
import RHFWrapper from "@/components/RHFWrapper";
import RhfRadio from "@/components/rhfFields/RhfRadio";
import RhfSelect from "@/components/rhfFields/RhfSelect";
import RhfInput from "@/components/rhfFields/RhfInput";
import RhfAsyncSelect from "@/components/rhfFields/RhfAsyncSelect";
import { SUPPORT_TICKET_TYPE_OPTIONS } from "./supportTicketConstants";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId?: string;
  patientDisplayName?: string;
  onCreated?: () => void;
};

export function SupportTicketCreateDialog({
  open,
  onOpenChange,
  patientId,
  patientDisplayName,
  onCreated,
}: Props) {
  const [btnLoading, setBtnLoading] = useState(false);
  const isPatientLocked = !!patientId;

  const handleCreateTicket = async (formData: Record<string, unknown>, methods: { reset?: () => void }) => {
    setBtnLoading(true);
    try {
      const payload = {
        patientId:
          patientId ||
          (formData?.patientId as { value?: string } | undefined)?.value ||
          formData?.patientId ||
          "",
        subject: String(formData?.subject ?? ""),
        ticketType: String(formData?.ticketType ?? ""),
        note: String(formData?.note ?? ""),
      };

      const { data } = await AxiosInstance().post("/impilo/support-tickets/create", payload);
      toast.success((data as { message?: string })?.message || "Support ticket created successfully.");
      onOpenChange(false);
      methods?.reset?.();
      onCreated?.();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      toast.error(err?.response?.data?.message || err?.message || "Failed to create ticket");
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <MyDialogContent heading="Create Support Ticket">
        <RHFWrapper
          initialValues={{
            subject: "patient",
            patientId: patientId
              ? { label: patientDisplayName || patientId, value: patientId }
              : null,
            ticketType: "",
            note: "",
          }}
          isValidate
          validateSchema={yup.object({
            subject: yup.string().required("Subject is required"),
            ticketType: yup.string().required("Ticket type is required"),
            note: yup.string().required("Note is required"),
            patientId: isPatientLocked
              ? yup.mixed().nullable()
              : yup.mixed().required("Patient is required"),
          })}
          onSubmit={handleCreateTicket}
        >
          <div>
            <RhfRadio
              name="subject"
              label="Subject"
              required
              className="px-2 py-2"
              options={[{ label: "Patient", value: "patient" }]}
            />

            {isPatientLocked ? (
              <div className="px-2 py-2">
                <p className="text-muted-foreground mb-1 text-sm">Patient</p>
                <p className="text-sm font-medium">{patientDisplayName || patientId}</p>
              </div>
            ) : (
              <div className="px-2 py-2">
                <RhfAsyncSelect
                  label="Patient"
                  name="patientId"
                  portalToBody={false}
                  storeOptionObject
                  asyncConfig={{
                    apiUrl: "/patients",
                    searchParam: "search",
                    minSearchLength: 3,
                    responseMapper: (response) =>
                      (response as { data?: { patients?: Record<string, unknown>[] } })?.data?.patients?.map(
                        (patient) => ({
                          label: `${patient?.patientId || ""} - ${[patient?.firstName, patient?.middleName, patient?.lastName].filter(Boolean).join(" ")}`.trim(),
                          value: String(patient?._id ?? ""),
                        }),
                      ) ?? [],
                  }}
                  placeholder="Search patient by name, ID, email, number..."
                  required
                />
              </div>
            )}

            <div className="flex flex-col gap-4 px-2 py-2">
              <RhfSelect
                name="ticketType"
                label="Ticket Type"
                required
                options={[...SUPPORT_TICKET_TYPE_OPTIONS]}
              />
            </div>

            <div className="flex flex-col gap-4 px-2 py-2">
              <RhfInput
                name="note"
                label="Note"
                required
                type="textarea"
                rows={5}
                placeholder="Describe the issue or add any relevant details..."
              />
            </div>

            <div className="sticky bottom-0 flex w-full justify-end bg-white px-2 py-2 pb-0">
              <Button type="submit" disabled={btnLoading} className="px-8">
                {btnLoading ? "Creating..." : "Create Ticket"}
              </Button>
            </div>
          </div>
        </RHFWrapper>
      </MyDialogContent>
    </Dialog>
  );
}
